import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminGetLegalDocumentVersion,
  useAdminUpsertLegalDocumentLink,
  useAdminActivateLegalDocumentVersion,
  getAdminGetLegalDocumentVersionQueryKey,
  getAdminGetLegalDocumentsQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Save, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { useLanguages, getLanguageName } from '@/hooks/useLanguages';

/** Required language code (English is always required for legal documents) */
const REQUIRED_LANGUAGE = 'en';

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  'privacy-policy': 'Privacy Policy',
  'terms-of-use': 'Terms of Use',
};

/**
 * Admin page for viewing and editing a specific legal document version
 */
export default function LegalDocumentVersionDetailPage() {
  const { type, version: versionParam } = useParams<{ type: string; version: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { languages } = useLanguages();

  const [activeLanguage, setActiveLanguage] = useState<string>('en');
  const [urlInputs, setUrlInputs] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState<Record<string, boolean>>({});
  const [initialized, setInitialized] = useState(false);

  // Initialize state when languages are loaded
  useEffect(() => {
    if (languages.length > 0 && Object.keys(urlInputs).length === 0) {
      const initialUrls: Record<string, string> = {};
      const initialChanges: Record<string, boolean> = {};
      languages.forEach((lang) => {
        initialUrls[lang.code] = '';
        initialChanges[lang.code] = false;
      });
      setUrlInputs(initialUrls);
      setHasChanges(initialChanges);
    }
  }, [languages, urlInputs]);

  const { data: versionData, isLoading } = useAdminGetLegalDocumentVersion(type!, versionParam!, {
    query: {
      enabled: !!type && !!versionParam,
    },
  });

  // Initialize URL inputs when data is loaded
  useEffect(() => {
    if (versionData?.data?.links && !initialized && languages.length > 0) {
      const newInputs: Record<string, string> = {};
      languages.forEach((lang) => {
        newInputs[lang.code] = '';
      });
      for (const link of versionData.data.links) {
        if (link.language && link.language in newInputs) {
          newInputs[link.language] = link.url || '';
        }
      }
      setUrlInputs(newInputs);
      setInitialized(true);
    }
  }, [versionData, initialized, languages]);

  const { mutate: upsertLink, isPending: isSaving } = useAdminUpsertLegalDocumentLink({
    mutation: {
      onSuccess: () => {
        toast.success(`${getLanguageName(languages, activeLanguage)} link saved`);
        queryClient.invalidateQueries({
          queryKey: getAdminGetLegalDocumentVersionQueryKey(type, versionParam),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetLegalDocumentsQueryKey(),
        });
        setHasChanges((prev) => ({ ...prev, [activeLanguage]: false }));
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpsertLegalDocumentLink', type, version: versionParam });
        toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: activateVersion, isPending: isActivating } = useAdminActivateLegalDocumentVersion({
    mutation: {
      onSuccess: () => {
        toast.success('Version activated successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminGetLegalDocumentVersionQueryKey(type, versionParam),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetLegalDocumentsQueryKey(),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'ActivateLegalDocumentVersion', type, version: versionParam });
        toast.error('Failed to activate: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  // Get URL from fetched data for a language
  const getSavedUrl = (lang: string): string => {
    const link = versionData?.data?.links?.find((l) => l.language === lang);
    return link?.url || '';
  };

  const handleUrlChange = (value: string) => {
    setUrlInputs((prev) => ({
      ...prev,
      [activeLanguage]: value,
    }));
    setHasChanges((prev) => ({
      ...prev,
      [activeLanguage]: value !== getSavedUrl(activeLanguage),
    }));
  };

  const handleSave = () => {
    const currentUrl = urlInputs[activeLanguage];
    if (!currentUrl.trim()) {
      toast.error('URL is required');
      return;
    }

    // Basic URL validation
    try {
      new URL(currentUrl.trim());
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    upsertLink({
      type: type!,
      version: versionParam!,
      data: {
        language: activeLanguage,
        url: currentUrl.trim(),
      },
    });
  };

  const handleActivate = () => {
    activateVersion({ type: type!, version: versionParam! });
  };

  const version = versionData?.data;
  const documentTitle = DOCUMENT_TYPE_LABELS[type || ''] || type;
  const isActive = version?.isActive;
  const hasEnglishLink = !!getSavedUrl('en');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!version) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/legal-documents')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Legal Documents
        </Button>
        <div className="text-center text-destructive py-8">
          <p>Version not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/legal-documents')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Legal Documents
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{documentTitle}</h1>
          <p className="text-muted-foreground">Version {versionParam}</p>
        </div>
        <div className="flex items-center gap-2">
          {isActive ? (
            <Badge variant="default">Active</Badge>
          ) : (
            <Badge variant="secondary">Inactive</Badge>
          )}
          {version.createdUtc && (
            <span className="text-sm text-muted-foreground">
              Created {new Date(version.createdUtc).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Document Links</CardTitle>
          <CardDescription>
            Set URLs for each language. English is required. Users will be redirected to these URLs
            to view the legal document.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="language-select">Language</Label>
            <Select value={activeLanguage} onValueChange={setActiveLanguage}>
              <SelectTrigger id="language-select" className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => {
                  const isRequired = lang.code === REQUIRED_LANGUAGE;
                  const hasSavedUrl = !!getSavedUrl(lang.code);
                  return (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        {lang.name}
                        {isRequired && <span className="text-xs text-destructive">*</span>}
                        {hasSavedUrl && <Check className="h-3 w-3 text-green-600" />}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {(() => {
            const currentLang = languages.find((l) => l.code === activeLanguage);
            const isRequired = activeLanguage === REQUIRED_LANGUAGE;
            if (!currentLang) return null;

            return (
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="document-url">
                    Document URL {isRequired && <span className="text-destructive">*</span>}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="document-url"
                      type="url"
                      value={urlInputs[activeLanguage] ?? ''}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder={`https://example.com/legal/${type}/${activeLanguage}`}
                    />
                    {getSavedUrl(activeLanguage) && (
                      <Button variant="outline" size="icon" asChild>
                        <a
                          href={getSavedUrl(activeLanguage)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enter the URL where the {currentLang.name} version of this document is hosted.
                    {!isRequired && ' Falls back to English if not provided.'}
                  </p>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges[activeLanguage]}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save Link'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {!isActive && (
        <Card>
          <CardHeader>
            <CardTitle>Activate Version</CardTitle>
            <CardDescription>
              Make this version the active one. Users will be required to accept this version.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={isActivating || !hasEnglishLink}>
                  <Check className="h-4 w-4 mr-2" />
                  {isActivating ? 'Activating...' : 'Activate Version'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Activate this version?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will make version {versionParam} the active version for {documentTitle}.
                    Users who haven't accepted this version will be prompted to do so.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleActivate} disabled={isActivating}>
                    {isActivating ? 'Activating...' : 'Activate'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {!hasEnglishLink && (
              <p className="text-sm text-destructive mt-2">
                An English link is required before activating this version.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
